import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DrawingRevisionSummary } from '../../types/drawing';
import { DrawingSaveState } from './useDrawingAutosave';

interface DrawingHeaderProps {
  name: string;
  onRename: (name: string) => void;
  saveState: DrawingSaveState;
  revisions: DrawingRevisionSummary[];
  onCheckpoint: (label: string) => Promise<void>;
  onRestore: (revision: number) => Promise<void>;
  onReload: () => void;
  /**
   * Opened from a project: the changes stay in the editor until the project is updated (and then go
   * to this drawing or a new one), so no renaming or checkpoints here.
   */
  fromProject?: boolean;
  /** Checkpoints need the drawing stored (a new one is, once it has something in it). */
  canCheckpoint?: boolean;
}

const SAVE_STATE_TONE: Record<DrawingSaveState, string> = {
  saved: 'text-gray-500',
  pending: 'text-gray-400',
  saving: 'text-gray-400',
  error: 'text-red-400',
  conflict: 'text-orange-300',
};

/** The drawing editor's sidebar header: name, save state and checkpoints. */
export function DrawingHeader({
  name,
  onRename,
  saveState,
  revisions,
  onCheckpoint,
  onRestore,
  onReload,
  fromProject = false,
  canCheckpoint = true,
}: DrawingHeaderProps) {
  const { t, i18n } = useTranslation();
  const [draftName, setDraftName] = useState(name);
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState<number | null>(null);

  const commitName = () => {
    const trimmed = draftName.trim();
    if (trimmed && trimmed !== name) onRename(trimmed);
    else setDraftName(name);
  };

  const checkpoint = async () => {
    setBusy(true);
    await onCheckpoint(label);
    setLabel('');
    setBusy(false);
  };

  const restore = async (revision: number) => {
    setConfirming(null);
    setBusy(true);
    await onRestore(revision);
    setBusy(false);
  };

  const formatDate = (value: string) =>
    new Date(value).toLocaleString(i18n.language, { dateStyle: 'short', timeStyle: 'short' });

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <input
          type="text"
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          onBlur={commitName}
          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
          maxLength={255}
          readOnly={fromProject}
          aria-label={t('drawings.name')}
          className="flex-1 min-w-0 h-[36px] rounded-lg bg-black border border-black focus:border-lime-400 focus:ring-2 focus:ring-lime-400/30 text-sm px-4 text-white transition outline-none"
        />
        <div className={`text-[11px] whitespace-nowrap ${SAVE_STATE_TONE[saveState]}`}>
          {t(fromProject && saveState === 'saved' ? 'drawings.save.unchanged' : `drawings.save.${saveState}`)}
          {saveState === 'conflict' && (
            <button onClick={onReload} className="ml-2 underline text-[#B3EC49]">
              {t('drawings.reload')}
            </button>
          )}
        </div>
      </div>

      {fromProject && <p className="text-[11px] text-gray-500">{t('drawings.fromProjectHint')}</p>}

      {!fromProject && canCheckpoint && (
        <button
          onClick={() => setOpen(!open)}
          className="self-start text-xs text-gray-400 hover:text-white flex items-center gap-1"
          aria-expanded={open}
        >
          <svg
            className={`w-3 h-3 transition-transform ${open ? 'rotate-90' : ''}`}
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M8 5l8 7-8 7z" />
          </svg>
          {t('drawings.checkpoints', { count: revisions.length })}
        </button>
      )}

      {open && !fromProject && canCheckpoint && (
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !busy && checkpoint()}
              placeholder={t('drawings.checkpointLabel')}
              maxLength={255}
              className="flex-1 min-w-0 h-8 rounded-lg bg-black border border-black focus:border-lime-400 text-xs px-3 text-white outline-none placeholder-[#747474]"
            />
            <button
              onClick={checkpoint}
              disabled={busy || saveState === 'conflict'}
              className="h-8 px-3 rounded-lg text-xs font-medium bg-[#313131] text-white hover:bg-[#3a3a3a] disabled:opacity-50"
            >
              {t('drawings.saveCheckpoint')}
            </button>
          </div>
          <ul className="max-h-[180px] overflow-y-auto custom-scrollbar flex flex-col gap-1">
            {revisions
              .slice()
              .reverse()
              .map((revision) => (
                <li
                  key={revision.revision}
                  className="flex items-center justify-between gap-2 rounded-lg bg-[#2a2a2a] px-3 py-1.5 text-xs"
                >
                  <div className="min-w-0">
                    <div className="text-gray-200 truncate">
                      {revision.label || t('drawings.checkpointNumber', { number: revision.revision })}
                    </div>
                    <div className="text-[10px] text-gray-500">{formatDate(revision.createdAt)}</div>
                  </div>
                  {confirming === revision.revision ? (
                    <div className="flex gap-2 whitespace-nowrap">
                      <button onClick={() => restore(revision.revision)} className="text-[#B3EC49] hover:underline">
                        {t('drawings.confirmRestore')}
                      </button>
                      <button onClick={() => setConfirming(null)} className="text-gray-400 hover:underline">
                        {t('common.cancel')}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirming(revision.revision)}
                      disabled={busy}
                      className="text-gray-300 hover:text-white whitespace-nowrap disabled:opacity-50"
                    >
                      {t('drawings.restore')}
                    </button>
                  )}
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
}
